<template>
  <ScModal
    :show="show"
    :class="'address-modal '+type+'Modal'"
    @close="close"
  >
    <form
      @submit.prevent="onSubmit"
      novalidate
    >
      <div class="modal-header">
        <h5 class="modal-title">
          {{ $t(`account.modal.${'address-'+type}`) }}
        </h5>
        <button
          type="button"
          class="close"
          @click="close"
        >
          <span>&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <div
          :class="{'form-input': true, 'form-checkbox-container': input.type === 'checkbox'}"
          v-for="(input, index) in modal.data"
          :key="'form-input-'+input.key"
        >
          <ScLabel>
            {{ $t(`account.labels.${input.key}`) }}
          </ScLabel>
          <ScSelect
            v-if="input.type==='select'"
            :class="{'is-invalid' : errors.has(input.key), [input.className]: true}"
            :name="input.key"
            track-by="id"
            label="name"
            :disabled="input.disabled || !input.options.length"
            v-validate="input.validation"
            :options="input.options"
            v-model="modal.data[index].value"
            :placeholder="$t(`account.placeholder.${input.key}`)"
          />
          <textarea
            v-else-if="input.type==='textarea'"
            class="form-control"
            :class="{'is-invalid' : errors.has(input.key)}"
            v-model="modal.data[index].value"
            v-validate="input.validation"
            :name="input.key"
            :placeholder="$t(`account.placeholder.${input.key}`)"
          />
          <ScCheckbox
            v-else-if="input.type==='checkbox'"
            class="form-checkbox"
            v-model="modal.data[index].value"
            v-validate="input.validation"
            :disabled="totalAddress === 1"
            :name="input.key"
          />
          <ScInput
            v-else
            :is-invalid="errors.has(input.key)"
            :type="input.type"
            :disabled="input.disabled"
            v-validate="input.validation"
            :data-vv-name="input.key"
            :name="input.key"
            v-model="modal.data[index].value"
            :placeholder="$t(`account.placeholder.${input.key}`)"
          />
          <span
            v-if="errors.has(input.key)"
            class="error-message"
          >
            {{ errors.first(input.key) }}
          </span>
        </div>
      </div>
      <div class="modal-footer">
        <ScButton
          type="submit"
          :variant="type==='edit'?'secondary':'primary'"
          class="form-button"
        >
          {{ $t(`account.save`) }}
        </ScButton>
      </div>
    </form>
  </ScModal>
</template>

<script>
import ScButton from '@trendyol/sc-ui-kit/ScButton';
import ScLabel from '@trendyol/sc-ui-kit/ScLabel';
import ScInput from '@trendyol/sc-ui-kit/ScInput';
import ScSelect from '@trendyol/sc-ui-kit/ScSelect';
import ScModal from '@trendyol/sc-ui-kit/ScModal';
import ScCheckbox from '@trendyol/sc-ui-kit/ScCheckbox';
import account from '@/services/account';

export default {
  name: 'AddressModal',
  components: { ScInput, ScButton, ScLabel, ScModal, ScSelect, ScCheckbox },
  data () {
    return {
      modal: {
        data: [],
      },
      cities: [],
      districts: [],
    };
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    type: {
      type: String,
      default: 'edit',
    },
    addressType: {
      type: String,
      default: 'N/A',
    },
    totalAddress: {
      type: Number,
      default: 0,
    },
  },
  watch: {
    async data (newValue) {
      if (!this.cities.length) {
        await this.getCities();
      }
      const validations = {
        postalCode: { },
        address: { required: 'Adres', minChar: [20, 'Adres'] },
        city: { required: 'İl', requiredSelect: ['İl', false] },
        district: { required: 'İlçe', requiredSelect: ['İlçe', false] },
        isDefault: { required: this.totalAddress >= 1 },
      };

      const cityValue = this.cities
        .find((city) => city.code.toString() === (newValue.cityID || '').toString());
      if (newValue.cityID) {
        await this.getDistricts(newValue.cityID);
      } else {
        this.districts = [];
      }
      const districtValue = this.districts
        .find((district) => district.id.toString() === (newValue.districtID || '').toString());

      const modalData = [
        {
          value: cityValue || {},
          key: 'city',
          type: 'select',
          options: this.cities,
          validation: validations.city,
          className: 'city-select',
        },
        {
          value: districtValue || {},
          key: 'district',
          type: 'select',
          options: this.districts,
          validation: validations.district,
          className: 'district-select',
        },
        {
          value: newValue.postalCode || '',
          key: 'postalCode',
          type: 'text',
          validation: validations.postalCode,
        },
        {
          value: newValue.address || '',
          key: 'address',
          type: 'textarea',
          validation: validations.address,
        },
        {
          value: this.totalAddress === 1
            ? true
            : newValue.isDefault || false,
          key: 'isDefault',
          type: 'checkbox',
          validation: validations.isDefault,
        },
      ];
      this.modal = {
        data: modalData,
      };
    },
    'modal.data.0': {
      async handler (newValue) {
        const district = this.modal.data[1];
        if (newValue.value.id) {
          if ((district.value.cityID || '').toString() !== newValue.value.id.toString()) {
            this.$set(this.modal.data[1], 'value', {});
            await this.getDistricts(newValue.value.code);
            this.$set(this.modal.data[1], 'options', this.districts);
          }
        } else {
          this.districts = [];
          this.$set(this.modal.data[1], 'value', {});
          this.$set(this.modal.data[1], 'options', this.districts);
        }
      },
      deep: true,
    },
  },
  methods: {
    async getCities () {
      this.cities = await account.getCities();
    },
    async getDistricts (cityCode) {
      this.districts = (await account.getDistricts(Number.parseInt(cityCode) + 99)).map(district => ({
        ...district,
        cityID: Number.parseInt(cityCode) + 99,
      }));
    },
    close () {
      this.errors.clear();
      this.$emit('close');
    },
    onSubmit () {
      return this.$validator.validate().then(valid => {
        if (!valid) {
          return false;
        }
        this.$emit('submit', { type: this.type, ...this.modal, address: this.data, addressType: this.addressType });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  .address-modal{
    position: fixed !important;
    & .modal-header{
      padding: 35px 30px 25px 30px;
      & .modal-title{
        font-size: 20px !important;
      }
    }
    & .modal-body{
      padding: 0 30px;
      & .form-input {
        &:not(:first-child) {
          margin-top: 20px;
        }
        & .city-select ::v-deep .mp-select-toggle,
        & .district-select ::v-deep .mp-select-toggle{
          height: 40px;
          transition: border-color 0s;
          border-radius: 6px;
        }
        & .city-select:not(.open):not(.is-invalid) ::v-deep .mp-select-toggle,
        & .district-select-select:not(.open):not(.is-invalid) ::v-deep .mp-select-toggle{
          border-color: #ced4da;
        }
        &::v-deep input{
          height: 40px;
        }
      }
      & .form-checkbox-container{
        display: flex;
        align-items: center;
        & .form-checkbox{
          margin-left: 8px;
        }
      }
    }

    & .modal-footer{
      padding: 30px;
      border-top: none;
      & .form-button {
        width: 100%;
        height: 40px;
      }
    }
    & textarea::placeholder{
      opacity:.5;
      font-size:12px;
      color:#273142
    }
    & .error-message{
      font-size:12px;
      color:#dc3545;
    }
  }
</style>
